export class GoogleMaps {
    static MARKER_BACKGROUND_FALLBACK = '#ff624c';
    static MARKER_BORDER_FALLBACK = '#e85945';
    static MARKER_GLYPH_FALLBACK = '#ffffff';

    constructor(mapEl) {
        if (!mapEl) return;

        this.mapWrapper = mapEl;
        this.init();
    }

    init() {
        this.mapInstance = null;
        this.markers = [];
        this.infoWindow = null;
        this.activeMarker = null;
        this.PinElement = null;
    }

    getCssColor(variable, fallback) {
        const value = getComputedStyle(document.documentElement)
            .getPropertyValue(variable)
            .trim();

        return value || fallback;
    }

    getMarkerColors() {
        const background =
            this.mapWrapper?.dataset?.markerBackground ||
            this.getCssColor(
                '--color-background-accent',
                GoogleMaps.MARKER_BACKGROUND_FALLBACK
            );
        const border =
            this.mapWrapper?.dataset?.markerBorder ||
            this.getCssColor(
                '--color-orange-600',
                GoogleMaps.MARKER_BORDER_FALLBACK
            );
        const glyph =
            this.mapWrapper?.dataset?.markerGlyph ||
            GoogleMaps.MARKER_GLYPH_FALLBACK;

        return { background, border, glyph };
    }

    createMarkerPin() {
        if (!this.PinElement) {
            return null;
        }

        const { background, border, glyph } = this.getMarkerColors();

        return new this.PinElement({
            background,
            borderColor: border,
            glyphColor: glyph,
        });
    }

    async initMap(id = null) {
        await google.maps.importLibrary('maps');
        const { AdvancedMarkerElement, PinElement } =
            await google.maps.importLibrary('marker');

        this.PinElement = PinElement;

        const points = JSON.parse(this.mapWrapper.getAttribute('data-point'));
        if (!points) return;

        this.markers = [];
        this.infoWindow = new google.maps.InfoWindow();

        const bounds = new google.maps.LatLngBounds();

        points.forEach(point => {
            bounds.extend({
                lat: parseFloat(point.lat),
                lng: parseFloat(point.lng),
            });
        });

        this.mapInstance = new google.maps.Map(this.mapWrapper, {
            zoom: 14,
            center: {
                lat: parseFloat(points[0].lat),
                lng: parseFloat(points[0].lng),
            },
            mapId: 'map',
        });

        if (points.length > 1) {
            this.mapInstance.fitBounds(bounds);
        }

        points.forEach(point => {
            const pin = this.createMarkerPin();

            const marker = new AdvancedMarkerElement({
                map: this.mapInstance,
                position: {
                    lat: parseFloat(point.lat),
                    lng: parseFloat(point.lng),
                },
                title: point.title,
                ...(pin ? { content: pin.element } : {}),
            });

            marker.addEventListener('click', () => {
                this.onMarkerClick(marker);
            });

            this.markers.push({
                id: point.id,
                marker,
                data: point,
            });
        });

        if (id) {
            const item = this.markers.find(m => m.id == id);
            if (item) {
                item.marker.click();
            }
        }
    }
    onMarkerClick(marker) {
        const item = this.markers.find(m => m.marker === marker);

        if (this.activeMarker === marker) {
            this.clearActiveMarker();
            return;
        }

        this.infoWindow.setContent(this.createMarkerContent(item.data));

        this.infoWindow.open({
            map: this.mapInstance,
            anchor: marker,
        });

        this.activeMarker = marker;
    }

    clearActiveMarker() {
        if (this.infoWindow) {
            this.infoWindow.close();
        }

        this.activeMarker = null;
    }

    goToMarker(id) {
        const item = this.markers.find(m => m.id == id);

        if (!item) return;

        const position = item.marker.position;

        this.mapInstance.panTo(position);
        this.mapInstance.setZoom(14);
        item.marker.click();
    }

    createMarkerContent(data) {
        return `
        <div class="flex flex-col gap-4 font-primary">
            <p class="text-small font-medium text-text-primary">${data.title}</p>
            <p class="text-small text-text-primary">${data.address}</p>
        </div>
    `;
    }
}
